// Boomin' redesign — shared primitives ported from screen-shared.jsx + boomin.jsx
// Keep design tokens here. All redesign pages import from this module.

import React, { CSSProperties, useEffect, useMemo } from "react";

// ── Design tokens ─────────────────────────────────────────────────────────
export const BRAND = "#FF4D2D";
export const BRAND_SOFT = "#FFE9E1";
export const ACCENT = "#FF7A52";
export const BG_CANVAS = "#FFFBF7";
export const TEXT = "#1A1A1A";

export type Palette = {
  brand: string;
  brandSoft: string;
  accent: string;
};

export const palette: Palette = {
  brand: BRAND,
  brandSoft: BRAND_SOFT,
  accent: ACCENT,
};

// ── One-shot keyframe injection (idempotent) ──────────────────────────────
const KEYFRAMES = `
  @keyframes boom-pop {
    0%   { transform: scale(0)    rotate(-12deg); opacity: 0; }
    60%  { transform: scale(1.18) rotate(4deg);   opacity: 1; }
    100% { transform: scale(1)    rotate(0);      opacity: 1; }
  }
  @keyframes boom-pulse {
    0%, 100% { transform: scale(1); }
    50%      { transform: scale(1.06); }
  }
  @keyframes boom-tick {
    0%, 100% { transform: scale(1); }
    50%      { transform: scale(1.05); }
  }
  @keyframes boom-shimmer {
    0%   { background-position: -200% 0; }
    100% { background-position:  200% 0; }
  }
  @keyframes boom-wiggle {
    0%, 100% { transform: rotate(-2.5deg); }
    50%      { transform: rotate( 2.5deg); }
  }
  @keyframes boom-fly-up {
    0%   { transform: translateY(24px) scale(0.9); opacity: 0; }
    100% { transform: translateY(0)    scale(1);   opacity: 1; }
  }
  @keyframes boom-rise {
    0%   { transform: translateY(10px); opacity: 0; }
    100% { transform: translateY(0);    opacity: 1; }
  }
  @keyframes boom-confetti-fall {
    0%   { transform: translate(0, -10px) rotate(0deg);    opacity: 1; }
    100% { transform: translate(var(--dx, 40px), 920px) rotate(720deg); opacity: 0; }
  }
  @keyframes boom-bg-pan {
    0%   { background-position: 0 0; }
    100% { background-position: 60px 60px; }
  }
  @keyframes boom-sparkle {
    0%, 100% { transform: scale(0.7) rotate(0deg);   opacity: 0.6; }
    50%      { transform: scale(1.1) rotate(20deg);  opacity: 1;   }
  }
  @keyframes boom-stamp {
    0%   { transform: scale(0) rotate(-30deg);    opacity: 0; }
    60%  { transform: scale(1.25) rotate(-14deg); opacity: 1; }
    100% { transform: scale(1) rotate(-14deg);    opacity: 1; }
  }
  @keyframes lp-marquee {
    0%   { transform: translateX(0); }
    100% { transform: translateX(-50%); }
  }
  @media (prefers-reduced-motion: reduce) {
    [data-boomin-anim] { animation: none !important; }
  }
`;

export function useBoominFx() {
  useEffect(() => {
    if (typeof document === "undefined") return;
    if (document.getElementById("boomin-fx")) return;
    const s = document.createElement("style");
    s.id = "boomin-fx";
    s.textContent = KEYFRAMES;
    document.head.appendChild(s);
  }, []);
}

// ── Responsive helper (matches prototype's isMobile heuristic) ────────────
export function useIsMobile(breakpoint = 720): boolean {
  const [m, setM] = React.useState<boolean>(() =>
    typeof window === "undefined" ? false : window.innerWidth < breakpoint
  );
  useEffect(() => {
    const onR = () => setM(window.innerWidth < breakpoint);
    window.addEventListener("resize", onR);
    return () => window.removeEventListener("resize", onR);
  }, [breakpoint]);
  return m;
}

// ── Pictograms (hand-drawn line icons) ────────────────────────────────────
export type PictName =
  | "cafe" | "ramen" | "sweet" | "bar" | "shop" | "salon" | "activity"
  | "pin" | "walk" | "chevron" | "chevronL" | "close" | "check"
  | "search" | "sliders" | "list" | "map" | "home" | "user"
  | "ticket" | "clock" | "bell" | "heart" | "sparkle" | "flame"
  | "qr" | "locate" | "plus" | "yen";

type PictProps = {
  name: PictName;
  size?: number;
  stroke?: string;
  strokeWidth?: number;
  fill?: string;
};

export const PictX: React.FC<PictProps> = ({
  name,
  size = 24,
  stroke = "#2D2D2D",
  strokeWidth = 1.6,
  fill = "none",
}) => {
  const p = {
    fill,
    stroke,
    strokeWidth,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
  };
  const map: Record<PictName, React.ReactNode> = {
    cafe: (
      <g {...p}>
        <path d="M5 9h13v6a4 4 0 0 1-4 4H9a4 4 0 0 1-4-4V9z" />
        <path d="M18 11h2a2 2 0 0 1 2 2v1a2 2 0 0 1-2 2h-2" />
        <path d="M8 4c-.5 1 .5 1.5 0 2.5M11 4c-.5 1 .5 1.5 0 2.5M14 4c-.5 1 .5 1.5 0 2.5" />
      </g>
    ),
    ramen: (
      <g {...p}>
        <path d="M3 12h20" />
        <path d="M4 12c.5 5 4 8 9 8s8.5-3 9-8" />
        <path d="M9 9c0-1.5 1-2 1-3M13 9c0-1.5 1-2 1-3M17 9c0-1.5 1-2 1-3" />
      </g>
    ),
    sweet: (
      <g {...p}>
        <path d="M13 4l2 4-4 1 3 3-4 1 3 3-4 1" />
        <path d="M3 18c4 3 14 3 18 0v3H3v-3z" />
      </g>
    ),
    bar: (
      <g {...p}>
        <path d="M5 4h14l-6 8v6" />
        <path d="M9 20h8" />
        <path d="M9 8h10" />
      </g>
    ),
    shop: (
      <g {...p}>
        <path d="M4 9l1.5-4h13L20 9" />
        <path d="M4 9v10h16V9" />
        <path d="M4 9c0 2 1.5 3 3 3s3-1 3-3 1.5 3 3 3 3-1 3-3 1.5 3 3 3" />
      </g>
    ),
    salon: (
      <g {...p}>
        <circle cx="7" cy="7" r="3" />
        <circle cx="17" cy="7" r="3" />
        <path d="M9 9l11 11M15 9L4 20" />
      </g>
    ),
    activity: (
      <g {...p}>
        <circle cx="12" cy="6" r="2" />
        <path d="M12 8v6M9 11l3 3 3-3M8 20l4-3 4 3" />
      </g>
    ),
    pin: (
      <g {...p}>
        <path d="M12 21s7-6.5 7-12a7 7 0 0 0-14 0c0 5.5 7 12 7 12z" />
        <circle cx="12" cy="9" r="2.5" />
      </g>
    ),
    walk: (
      <g {...p}>
        <circle cx="13" cy="5" r="1.6" />
        <path d="M11 8l-2 4 3 2v6M14 14l3 1M9 12l-3 1" />
      </g>
    ),
    chevron: (
      <g {...p}>
        <path d="M9 6l6 6-6 6" />
      </g>
    ),
    chevronL: (
      <g {...p}>
        <path d="M15 6l-6 6 6 6" />
      </g>
    ),
    close: (
      <g {...p}>
        <path d="M6 6l12 12M18 6L6 18" />
      </g>
    ),
    check: (
      <g {...p}>
        <path d="M5 12l5 5L20 7" />
      </g>
    ),
    search: (
      <g {...p}>
        <circle cx="11" cy="11" r="6" />
        <path d="M16 16l4 4" />
      </g>
    ),
    sliders: (
      <g {...p}>
        <path d="M4 6h11M4 12h7M4 18h11" />
        <circle cx="18" cy="6" r="2" />
        <circle cx="14" cy="12" r="2" />
        <circle cx="18" cy="18" r="2" />
      </g>
    ),
    list: (
      <g {...p}>
        <path d="M8 6h13M8 12h13M8 18h13" />
        <circle cx="4" cy="6" r="0.7" fill={stroke} />
        <circle cx="4" cy="12" r="0.7" fill={stroke} />
        <circle cx="4" cy="18" r="0.7" fill={stroke} />
      </g>
    ),
    map: (
      <g {...p}>
        <path d="M3 6l6-2 6 2 6-2v14l-6 2-6-2-6 2V6z" />
        <path d="M9 4v16M15 6v16" />
      </g>
    ),
    home: (
      <g {...p}>
        <path d="M4 11l8-7 8 7v9H4z" />
        <path d="M10 20v-5h4v5" />
      </g>
    ),
    user: (
      <g {...p}>
        <circle cx="12" cy="9" r="3.5" />
        <path d="M5 20c1-3.5 4-5 7-5s6 1.5 7 5" />
      </g>
    ),
    ticket: (
      <g {...p}>
        <path d="M3 9a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v1a2 2 0 0 0 0 4v1a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-1a2 2 0 0 0 0-4V9z" />
        <path d="M10 7v10" strokeDasharray="2 2" />
      </g>
    ),
    clock: (
      <g {...p}>
        <circle cx="12" cy="12" r="8" />
        <path d="M12 8v4l3 2" />
      </g>
    ),
    bell: (
      <g {...p}>
        <path d="M6 16h12l-1.5-2V10a4.5 4.5 0 0 0-9 0v4L6 16z" />
        <path d="M10 19a2 2 0 0 0 4 0" />
      </g>
    ),
    heart: (
      <g {...p}>
        <path d="M12 19s-7-4.2-7-9.5A4 4 0 0 1 12 7a4 4 0 0 1 7 2.5C19 14.8 12 19 12 19z" />
      </g>
    ),
    sparkle: (
      <g {...p}>
        <path d="M12 4v6M12 14v6M4 12h6M14 12h6" />
        <path d="M7 7l3 3M14 14l3 3M7 17l3-3M14 10l3-3" strokeWidth={1.2} />
      </g>
    ),
    flame: (
      <g {...p}>
        <path d="M12 21c4 0 7-3 7-7 0-3-2-5-3-6 0 2-1 3-2 3 0-3-1-6-3-7-1 4-5 5-5 10 0 4 3 7 6 7z" />
      </g>
    ),
    qr: (
      <g {...p}>
        <rect x="4" y="4" width="6" height="6" />
        <rect x="14" y="4" width="6" height="6" />
        <rect x="4" y="14" width="6" height="6" />
        <path
          d="M14 14h2v2h-2zM18 14h2v2h-2zM14 18h2v2h-2zM18 18h2v2h-2z"
          fill={stroke}
          stroke="none"
        />
      </g>
    ),
    locate: (
      <g {...p}>
        <circle cx="12" cy="12" r="3.2" />
        <circle cx="12" cy="12" r="8" />
        <path d="M12 2v3M12 19v3M2 12h3M19 12h3" />
      </g>
    ),
    plus: (
      <g {...p}>
        <path d="M12 5v14M5 12h14" />
      </g>
    ),
    yen: (
      <g {...p}>
        <path d="M7 5l5 7 5-7M7 13h10M7 16h10M12 12v8" />
      </g>
    ),
  };
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      {map[name]}
    </svg>
  );
};

// ── Confetti burst (falling pieces over a positioned ancestor) ────────────
type ConfettiProps = {
  density?: number;
  palette?: string[];
};

export const ConfettiBurst: React.FC<ConfettiProps> = React.memo(
  function ConfettiBurst({ density = 36, palette: pal }) {
    const items = useMemo(() => {
      const colors =
        pal || ["#FFE05A", "#FF6B35", "#FF9966", "#3DBED9", "#FFC9B0", "#FFFFFF", "#0FAC6D"];
      return Array.from({ length: density }).map((_, i) => ({
        id: i,
        left: Math.random() * 100,
        delay: Math.random() * 5,
        dur: 3.2 + Math.random() * 3,
        dx: Math.round((Math.random() - 0.5) * 220),
        size: 6 + Math.random() * 10,
        color: colors[i % colors.length],
        shape: i % 3,
      }));
    }, [density, pal]);

    return (
      <div
        style={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          overflow: "hidden",
          zIndex: 1,
        }}
      >
        {items.map((it) => (
          <div
            key={it.id}
            data-boomin-anim
            style={
              {
                position: "absolute",
                left: `${it.left}%`,
                top: -20,
                width: it.size,
                height: it.size * (it.shape === 0 ? 0.5 : 1),
                background: it.color,
                borderRadius: it.shape === 2 ? "50%" : 2,
                animation: `boom-confetti-fall ${it.dur}s ${it.delay}s linear infinite`,
                ["--dx" as any]: `${it.dx}px`,
                boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
              } as CSSProperties
            }
          />
        ))}
      </div>
    );
  }
);

// ── Sparkle (twinkling star) ──────────────────────────────────────────────
type SparkleProps = {
  style?: CSSProperties;
  delay?: number;
  color?: string;
  size?: number;
};

export const Sparkle: React.FC<SparkleProps> = ({
  style,
  delay = 0,
  color = "#FFC325",
  size = 16,
}) => (
  <div
    data-boomin-anim
    style={{
      position: "absolute",
      width: size,
      height: size,
      animation: `boom-sparkle 1.6s ${delay}ms ease-in-out infinite`,
      ...style,
    }}
  >
    <svg width={size} height={size} viewBox="0 0 16 16">
      <path
        d="M8 1.5 L 9 7 L 14.5 8 L 9 9 L 8 14.5 L 7 9 L 1.5 8 L 7 7 Z"
        fill={color}
      />
    </svg>
  </div>
);

// ── Bottom navigation (mobile app screens) ────────────────────────────────
type BottomNavProps = {
  active: "home" | "map" | "tickets" | "me";
  brand: string;
};

export const BottomNavX: React.FC<BottomNavProps> = ({ active, brand }) => {
  const items: { id: BottomNavProps["active"]; label: string; icon: PictName; href: string }[] = [
    { id: "home", label: "さがす", icon: "home", href: "/discover" },
    { id: "map", label: "マップ", icon: "map", href: "/map" },
    { id: "tickets", label: "マイクーポン", icon: "ticket", href: "/wallet" },
    { id: "me", label: "マイページ", icon: "user", href: "/me" },
  ];
  return (
    <div
      style={{
        position: "fixed",
        left: 0,
        right: 0,
        bottom: 0,
        paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 12px)",
        paddingTop: 6,
        background: "rgba(255,255,255,0.96)",
        backdropFilter: "blur(20px)",
        WebkitBackdropFilter: "blur(20px)",
        borderTop: "0.5px solid rgba(0,0,0,0.08)",
        display: "flex",
        justifyContent: "space-around",
        zIndex: 30,
        maxWidth: 640,
        margin: "0 auto",
      }}
    >
      {items.map((it) => {
        const on = active === it.id;
        return (
          <a
            key={it.id}
            href={it.href}
            style={{
              background: "transparent",
              border: 0,
              cursor: "pointer",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 3,
              padding: "6px 10px",
              textDecoration: "none",
            }}
          >
            <PictX
              name={it.icon}
              size={22}
              stroke={on ? brand : "#999"}
              strokeWidth={1.7}
            />
            <span
              style={{
                fontSize: 10,
                color: on ? brand : "#999",
                fontWeight: 700,
              }}
            >
              {it.label}
            </span>
          </a>
        );
      })}
    </div>
  );
};

// ── Section helpers (LP + Partners share these) ───────────────────────────
export const SectionEyebrow: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div
    style={{
      fontSize: 12,
      fontWeight: 800,
      color: "#888",
      letterSpacing: "0.2em",
      marginBottom: 14,
    }}
  >
    {children}
  </div>
);

export const SectionH2: React.FC<{
  children: React.ReactNode;
  isMobile: boolean;
  large?: boolean;
}> = ({ children, isMobile, large }) => (
  <h2
    style={{
      fontSize: isMobile ? 32 : large ? 52 : 48,
      fontWeight: 800,
      lineHeight: 1.2,
      margin: 0,
      letterSpacing: "-0.01em",
    }}
  >
    {children}
  </h2>
);

// ── Color utilities ───────────────────────────────────────────────────────
export function shade(hex: string, amount: number): string {
  const n = parseInt(hex.slice(1), 16);
  const r = Math.max(0, Math.min(255, (n >> 16) - amount));
  const g = Math.max(0, Math.min(255, ((n >> 8) & 0xff) - amount));
  const b = Math.max(0, Math.min(255, (n & 0xff) - amount));
  return "#" + ((r << 16) | (g << 8) | b).toString(16).padStart(6, "0");
}

// ── Shared brand header for PWA screens ──────────────────────────────────
// Slim sticky bar with the Boomin' brand mark linking back to the LP root.
// A hamburger menu on the right gives every page a path to マイクーポン /
// マイページ (and the rest of the primary nav). Pages may pass `trailing`
// for page-specific controls placed just before the menu.
export const AppHeader: React.FC<{ trailing?: React.ReactNode }> = ({ trailing }) => (
  <header
    style={{
      padding: "calc(env(safe-area-inset-top, 0px) + 10px) 16px 10px",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      background: "rgba(255,251,247,0.96)",
      backdropFilter: "blur(12px)",
      WebkitBackdropFilter: "blur(12px)",
      borderBottom: "1px solid #F0E6DC",
      position: "sticky",
      top: 0,
      zIndex: 25,
    }}
  >
    <a
      href="/"
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        textDecoration: "none",
        color: "#1A1A1A",
        fontSize: 18,
        fontWeight: 800,
        letterSpacing: "-0.01em",
      }}
    >
      <span
        style={{
          width: 26,
          height: 26,
          borderRadius: 8,
          background: BRAND,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <PictX name="walk" size={15} stroke="#fff" strokeWidth={2} />
      </span>
      Boomin<span style={{ color: BRAND }}>'</span>
    </a>
    <div style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
      {trailing}
      <HeaderMenu />
    </div>
  </header>
);

// ── Hamburger menu (global nav, available on every AppHeader page) ────────
const HEADER_MENU_ITEMS: { label: string; icon: PictName; href: string }[] = [
  { label: "さがす", icon: "home", href: "/discover" },
  { label: "マップ", icon: "map", href: "/map" },
  { label: "マイクーポン", icon: "ticket", href: "/wallet" },
  { label: "マイページ", icon: "user", href: "/me" },
];

export const HeaderMenu: React.FC = () => {
  const [open, setOpen] = React.useState(false);

  // Close on Escape for keyboard users.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <div style={{ position: "relative" }}>
      <button
        type="button"
        aria-label="メニュー"
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen((v) => !v)}
        style={{
          width: 34,
          height: 34,
          borderRadius: 999,
          border: "1px solid #F0E6DC",
          background: open ? BRAND_SOFT : "#fff",
          cursor: "pointer",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke={open ? BRAND : "#1A1A1A"}
          strokeWidth="1.9"
          strokeLinecap="round"
        >
          <path d="M4 7h16M4 12h16M4 17h16" />
        </svg>
      </button>

      {open && (
        <>
          {/* Click-away backdrop */}
          <div
            onClick={() => setOpen(false)}
            style={{ position: "fixed", inset: 0, zIndex: 40 }}
          />
          <nav
            role="menu"
            aria-label="メインメニュー"
            style={{
              position: "absolute",
              top: "calc(100% + 8px)",
              right: 0,
              minWidth: 180,
              background: "#fff",
              border: "1px solid #F0E6DC",
              borderRadius: 14,
              boxShadow: "0 12px 28px rgba(0,0,0,0.14)",
              padding: 6,
              zIndex: 41,
            }}
          >
            {HEADER_MENU_ITEMS.map((it) => (
              <a
                key={it.href}
                href={it.href}
                role="menuitem"
                onClick={() => setOpen(false)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "10px 12px",
                  borderRadius: 10,
                  textDecoration: "none",
                  color: "#1A1A1A",
                  fontSize: 14,
                  fontWeight: 700,
                }}
              >
                <PictX name={it.icon} size={18} stroke={BRAND} strokeWidth={1.8} />
                {it.label}
              </a>
            ))}
          </nav>
        </>
      )}
    </div>
  );
};

// ── Shared mini footer for PWA screens ───────────────────────────────────
// Appears above the bottom nav. Always provides a path back to the LP root.
export const AppFooter: React.FC<{ withBottomNav?: boolean }> = ({
  withBottomNav,
}) => (
  <footer
    style={{
      padding: "28px 20px 24px",
      marginBottom: withBottomNav ? 80 : 0,
      borderTop: "1px solid #F0E6DC",
      background: "#FFFBF7",
      textAlign: "center",
      fontSize: 11,
      color: "#888",
      lineHeight: 1.8,
    }}
  >
    <a
      href="/"
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        color: "#666",
        fontWeight: 700,
        textDecoration: "none",
        fontSize: 12,
      }}
    >
      Powered by
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
          color: "#1A1A1A",
        }}
      >
        <span
          style={{
            width: 18,
            height: 18,
            borderRadius: 6,
            background: BRAND,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <PictX name="walk" size={11} stroke="#fff" strokeWidth={2} />
        </span>
        Boomin<span style={{ color: BRAND }}>'</span>
      </span>
    </a>
    <div style={{ marginTop: 8 }}>© 2026 Rebase, Inc. All rights reserved.</div>
  </footer>
);

export function shadeMix(a: string, b: string, ratio: number): string {
  const pa = parseInt(a.slice(1), 16);
  const pb = parseInt(b.slice(1), 16);
  const ar = pa >> 16,
    ag = (pa >> 8) & 0xff,
    ab = pa & 0xff;
  const br = pb >> 16,
    bg = (pb >> 8) & 0xff,
    bb = pb & 0xff;
  const r = Math.round(ar * (1 - ratio) + br * ratio);
  const g = Math.round(ag * (1 - ratio) + bg * ratio);
  const bl = Math.round(ab * (1 - ratio) + bb * ratio);
  return "#" + ((r << 16) | (g << 8) | bl).toString(16).padStart(6, "0");
}
